import type { ReactNode } from 'react';
import { flagOf, languageCountry } from '@cp/shared';
import { countryName, languageName } from '@/utils/market';

type FlagSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * A country or a language as its flag (ADR-0060), named on hover and to screen
 * readers. A country wins over a language when both are given; nothing is
 * drawn for a code the panel cannot place.
 */
export function Flag({ country, language, size = 'md' }: { readonly country?: string | null; readonly language?: string | null; readonly size?: FlagSize }): ReactNode {
  const code = country ?? languageCountry(language);
  const emoji = flagOf(code);
  if (!emoji) return null;
  const name = country ? (countryName(country.toUpperCase()) ?? country) : language ? (languageName(language) ?? language) : '';
  return (
    <span className={`flag flag-${size}`} role="img" aria-label={name} title={name}>
      {emoji}
    </span>
  );
}

/** «🇩🇪 Германия (DE) · немецкий (de)»: a site's market as the panel shows it everywhere. */
export function MarketName({ country, language, codes = true }: { readonly country: string | null; readonly language: string | null; readonly codes?: boolean }): ReactNode {
  if (!country && !language) return null;
  const parts = [
    country ? `${countryName(country) ?? country}${codes ? ` (${country})` : ''}` : null,
    language ? `${languageName(language) ?? language}${codes ? ` (${language})` : ''}` : null,
  ].filter(Boolean);
  return (
    <span className="with-flag">
      <Flag country={country} language={language} />
      <span>{parts.join(' · ')}</span>
    </span>
  );
}

/** «🇰🇷 корейский (ko)»: a language on its own, with the flag of where it is spoken. */
export function LanguageName({ language, code = true }: { readonly language: string; readonly code?: boolean }): ReactNode {
  return (
    <span className="with-flag">
      <Flag language={language} />
      <span>
        {languageName(language) ?? language}
        {code ? ` (${language})` : ''}
      </span>
    </span>
  );
}
